import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useServices } from '../services/context.js';
import type { RecordedMatchRead, RecordedMatchResult } from '../services/types.js';

function outcomeLabel(result: RecordedMatchResult): string {
  const outcome = result.outcome;
  switch (outcome.kind) {
    case 'slot-victory': return `${result.players[outcome.winnerSlot]?.nickname ?? 'Player'} won`;
    case 'team-victory': return `Team ${outcome.winnerTeam + 1} won`;
    case 'draw': return 'Draw';
    case 'placement': return 'Final placements';
    case 'completed': return 'Match completed';
    case 'no-contest': return 'No contest';
  }
}
const statusText = {
  'not-found': 'This saved match could not be found.',
  pending: 'The match result is still being saved. Check again shortly.',
  unavailable: 'This result is unavailable. Its publication may no longer be available, or the service may be temporarily unreachable.',
  conflict: 'This result could not be verified. It cannot be displayed safely.',
};

/** Historical result only: the server verifies the saved publication, never the current active version. */
export function RecordedMatch() {
  const { slug = '', matchId = '' } = useParams();
  const services = useServices();
  const key = `${slug}/${matchId}`;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ key: string; title?: string; read: RecordedMatchRead }>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    async function load() {
      let title: string | undefined;
      let read: RecordedMatchRead;
      try {
        const creationId = await services.repos.creations.resolveSlug(slug);
        if (!creationId) read = { status: 'not-found' };
        else {
          const creation = await services.repos.creations.get(creationId);
          if (!creation || creation.visibility === 'draft') read = { status: 'unavailable' };
          else {
            title = creation.title;
            // No active-version comparison: a saved result remains pinned to its own version.
            read = await services.recordedMatchResult(creationId, matchId);
          }
        }
      } catch { read = { status: 'unavailable' }; }
      if (active) { setState({ key, title, read }); setLoading(false); }
    }
    void load();
    return () => { active = false; };
  }, [services, slug, matchId, key, attempt]);
  const current = state?.key === key ? state : undefined;
  const read = current?.read;
  return (
    <div className="container">
      <Link to={`/g/${encodeURIComponent(slug)}`}>Back to game</Link>
      <h1>Saved match result</h1>
      {current?.title && <h2>{current.title}</h2>}
      {loading || !read ? <p role="status" aria-live="polite">Loading saved match…</p> :
        read.status === 'result' || read.status === 'no-contest' ? (
          <section className="panel" aria-label="Saved match result">
            <h2>{outcomeLabel(read.result)}</h2>
            <p>Saved game version: v{read.result.version} · Unranked</p>
            <p>Duration: {(read.result.durationMs / 1000).toFixed(1)} seconds</p>
            <table className="table">
              <caption>Final scores</caption>
              <thead><tr><th scope="col">Player</th><th scope="col">Team</th><th scope="col">Score</th>
                {read.result.outcome.kind === 'placement' && <th scope="col">Place</th>}</tr></thead>
              <tbody>{read.result.players.map(player => <tr key={player.slot}>
                <th scope="row">{player.nickname}</th><td>{read.result.teamBySlot[player.slot] + 1}</td>
                <td>{read.result.scores[player.slot]}</td>
                {read.result.outcome.kind === 'placement' && <td>{read.result.outcome.rankBySlot[player.slot]}</td>}
              </tr>)}</tbody>
            </table>
            <p role="status">Playback unavailable for this saved match.</p>
          </section>
        ) : <p role={read.status === 'pending' ? 'status' : 'alert'}>{statusText[read.status]}</p>}
      {!loading && read && read.status !== 'result' && read.status !== 'no-contest' && read.status !== 'not-found' &&
        <button type="button" className="btn" onClick={() => setAttempt(value => value + 1)}>Check again</button>}
    </div>
  );
}
